<template>
  <div class="page-container">
    <div class="header-section">
      <h1 class="page-title">My Pay Stubs</h1>
      <p class="page-subtitle">View and download your pay stubs history</p>
    </div>

    <div v-if="!loading && payStubs.length > 0" class="year-filter">
      <label for="stubYear">Year</label>
      <select id="stubYear" v-model="selectedYear">
        <option v-for="y in stubYears" :key="y" :value="y">{{ y }}</option>
      </select>
    </div>

    <!-- YTD Summary Cards (for the selected year) -->
    <div v-if="!loading && payStubs.length > 0" class="ytd-cards-section">
      <div class="ytd-card ytd-card-blue">
        <div class="card-header">
          <h3 class="card-label">YTD Gross Wages</h3>
          <span class="card-icon">💰</span>
        </div>
        <div class="card-amount">{{ currency(latestYTD.grossWages) }}</div>
      </div>

      <div class="ytd-card ytd-card-green">
        <div class="card-header">
          <h3 class="card-label">YTD Net Pay</h3>
          <span class="card-icon">📈</span>
        </div>
        <div class="card-amount">{{ currency(latestYTD.netWages) }}</div>
      </div>

      <div class="ytd-card ytd-card-orange">
        <div class="card-header">
          <h3 class="card-label">YTD Deductions</h3>
          <span class="card-icon">📋</span>
        </div>
        <div class="card-amount">{{ currency(latestYTD.deductions) }}</div>
      </div>
    </div>

    <div v-if="loading" class="state-message">
      <div class="loader"></div>
      Loading your pay stubs...
    </div>

    <div v-else-if="error" class="state-message error">
      {{ error }}
    </div>

    <div v-else-if="payStubs.length === 0" class="state-message empty">
      <div class="empty-icon">📄</div>
      <h3>No Pay Stubs Found</h3>
      <p>You don't have any pay stubs available yet.</p>
    </div>

    <div v-else class="content-card">
      <div class="table-responsive">
        <table class="data-table">
          <thead>
            <tr>
              <th>Pay Period</th>
              <th>Pay Date</th>
              <th>Property</th>
              <th class="text-right">Gross Wages</th>
              <th class="text-right">Net Pay</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="yearStubs.length === 0">
              <td colspan="6" class="no-stubs-for-year">No pay stubs for {{ selectedYear }} yet.</td>
            </tr>
            <tr v-for="stub in yearStubs" :key="stub.id">
              <td>
                <span class="period-dates">
                  {{ formatDate(stub.payPeriodStart) }} - {{ formatDate(stub.payPeriodEnd) }}
                </span>
              </td>
              <td>{{ formatDate(stub.payDate) }}</td>
              <td>{{ stub.propertyName || '—' }}</td>
              <td class="text-right amount">{{ currency(stub.grossWages) }}</td>
              <td class="text-right amount highlight">{{ currency(stub.netWages) }}</td>
              <td>
                <div class="row-actions">
                  <router-link
                    :to="{ name: 'MyPayStubDetails', params: { id: stub.id } }"
                    class="action-btn"
                  >
                    View
                  </router-link>
                  <router-link
                    :to="{ name: 'MyPayStubDetails', params: { id: stub.id }, query: { download: '1' } }"
                    class="action-btn action-btn--pdf"
                    :aria-label="`Download PDF of pay stub paid ${formatDate(stub.payDate)}`"
                  >
                    PDF
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { auth, db } from '@/firebase'
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  limit 
} from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { getYear, yearOptions, toLocalDate } from '@/utils/dates'

const payStubs = ref([])
const loading = ref(true)
const error = ref(null)
const selectedYear = ref(new Date().getFullYear())
let unsubscribe = null

const stubYears = computed(() => yearOptions(payStubs.value, 'payDate'))

// Stubs for the selected year (payStubs is already sorted newest first)
const yearStubs = computed(() =>
  payStubs.value.filter(stub => getYear(stub.payDate) === selectedYear.value)
)

// YTD totals come from the latest stub in the selected year, so a new year starts at $0
const latestYTD = computed(() => {
  if (yearStubs.value.length === 0) {
    return { grossWages: 0, netWages: 0, deductions: 0 }
  }
  const latest = yearStubs.value[0]
  return {
    grossWages: latest.ytdGrossWages || 0,
    netWages: latest.ytdNetWages || 0,
    deductions: (latest.ytdFederalIncome || 0) + 
                (latest.ytdSocialSecurity || 0) + 
                (latest.ytdMedicare || 0) + 
                (latest.ytdRent || 0)
  }
})

// Helpers
const formatDate = (val) => {
  if (!val) return '—'
  // Firestore Timestamp or 'YYYY-MM-DD' (read as a local date, not UTC)
  const date = toLocalDate(val)
  return new Intl.DateTimeFormat('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  }).format(date)
}

const currency = (val) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(val || 0)
}

onMounted(() => {
  unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      loading.value = false
      return
    }

    try {
      // 1. Find the employee record linked to this user's email
      const employeesRef = collection(db, 'employees')
      const empQuery = query(employeesRef, where('email', '==', user.email), limit(1))
      const empSnap = await getDocs(empQuery)

      if (empSnap.empty) {
        // Fallback: Try searching by lowercase if casing mismatch
        // Or if your system links by ID separately? current plan assumes email link
        console.warn('No employee record found for email:', user.email)
        loading.value = false
        return
      }

      const employeeDoc = empSnap.docs[0]
      const employeeId = employeeDoc.id // Firestore Document ID

      // 2. Query payStubs for this employeeId
      // Note: CreatePayStubPage saves 'employeeId' as the firestore doc ID.
      const stubsRef = collection(db, 'payStubs')
      // Removed orderBy to avoid missing index error. Sorting client-side instead.
      const stubsQuery = query(
        stubsRef, 
        where('employeeId', '==', employeeId)
      )

      const stubsSnap = await getDocs(stubsQuery)
      const fetchedStubs = stubsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // Sort by payDate descending (newest first)
      payStubs.value = fetchedStubs.sort((a, b) => {
        const dateA = a.payDate?.toDate ? a.payDate.toDate() : new Date(a.payDate || 0)
        const dateB = b.payDate?.toDate ? b.payDate.toDate() : new Date(b.payDate || 0)
        return dateB - dateA
      })

    } catch (err) {
      console.error('Error fetching pay stubs:', err)
      error.value = 'Failed to load pay stubs. Please contact support if this persists.' // Generic friendly error
    } finally {
      loading.value = false
    }
  })
})

import { onUnmounted } from 'vue'
onUnmounted(() => {
  if (unsubscribe) unsubscribe()
})
</script>

<style scoped>
.page-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: 'Inter', sans-serif;
  color: #1f2937;
}

.header-section {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: #6b7280;
  font-size: 1.1rem;
}

.year-filter {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 500;
  color: #374151;
}

.year-filter select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
}

.no-stubs-for-year {
  text-align: center;
  color: #6b7280;
  padding: 2rem 1rem;
}

/* YTD Summary Cards */
.ytd-cards-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.ytd-card {
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.ytd-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.ytd-card-blue {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: #93c5fd;
}

.ytd-card-green {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  border-color: #86efac;
}

.ytd-card-orange {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  border-color: #fb923c;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-label {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ytd-card-blue .card-label {
  color: #0369a1;
}

.ytd-card-green .card-label {
  color: #059669;
}

.ytd-card-orange .card-label {
  color: #b45309;
}

.card-icon {
  font-size: 1.5rem;
  opacity: 0.8;
}

.card-amount {
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
  line-height: 1;
}

.ytd-card-blue .card-amount {
  color: #0c4a6e;
}

.ytd-card-green .card-amount {
  color: #065f46;
}

.ytd-card-orange .card-amount {
  color: #92400e;
}

/* States */
.state-message {
  text-align: center;
  padding: 4rem 2rem;
  background: #f9fafb;
  border-radius: 1rem;
  color: #6b7280;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.loader {
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid #3b82f6;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

/* Table Section */
.content-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.table-responsive {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.data-table th {
  background: #f8fafc;
  padding: 1rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 0.95rem;
}

.data-table tr:hover {
  background-color: #f8fafc;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.text-right { text-align: right; }

.period-dates {
  font-weight: 500;
  color: #374151;
}

.amount {
  font-family: 'Roboto Mono', monospace; /* or standard mono */
  font-weight: 500;
}

.highlight {
  color: #059669; /* emerald-600 */
  font-weight: 600;
}

.row-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn--pdf {
  background-color: #2563eb;
  color: #fff;
}

.action-btn--pdf:hover {
  background-color: #1d4ed8;
  color: #fff;
}

.action-btn {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #eff6ff;
  color: #2563eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.action-btn:hover {
  background-color: #dbeafe;
  color: #1d4ed8;
}

@media (max-width: 640px) {
  .page-title { font-size: 2rem; }
  .data-table th, .data-table td { padding: 0.75rem 1rem; }
  
  .ytd-cards-section {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  .card-amount {
    font-size: 1.5rem;
  }
}
</style>
